import { useState, useEffect, useCallback } from "react";

// ── Types ─────────────────────────────────────────────────────

type EntityType = "developer" | "pipeline" | "agent" | "host" | "service_account" | "team";
type Tab = "entities" | "roles" | "test";
type Decision = "allow" | "deny" | "warn";
type GuardrailSeverity = "low" | "medium" | "high" | "critical";

interface EntityRole {
  role_id: string;
  name: string;
}

interface Entity {
  id: string;
  name: string;
  entity_type: EntityType;
  roles: EntityRole[];
  budget_usd: number;
  active: boolean;
}

interface Guardrail {
  id: string;
  description: string;
  severity: GuardrailSeverity;
}

interface Role {
  id: string;
  name: string;
  capabilities: string[];
  guardrails: Guardrail[];
  budget_limit_usd: number;
  allowed_entity_types: EntityType[];
  locked: boolean;
}

interface CheckResult {
  decision: Decision;
  reason: string;
  entity_id: string;
  capability_id: string;
}

interface GrantedCapability {
  capability_id: string;
  source_role: string;
}

// ── Palette ───────────────────────────────────────────────────

const BG = "#282a36";
const CARD_BG = "rgba(30, 30, 46, 0.8)";
const BORDER = "rgba(255,255,255,0.06)";
const ACCENT = "#50fa7b";
const TEXT = "#f8f8f2";
const TEXT_DIM = "#6272a4";
const TEXT_BRIGHT = "#f8f8f2";
const WARN = "#f1fa8c";
const ERROR = "#ff5555";
const FONT = "'JetBrains Mono', 'Fira Code', monospace";

const ENTITY_TYPE_COLORS: Record<EntityType, string> = {
  developer: "#50fa7b",
  pipeline: "#8be9fd",
  agent: "#bd93f9",
  host: "#8be9fd",
  service_account: "#ffb86c",
  team: "#ff5555",
};

const SEVERITY_COLORS: Record<GuardrailSeverity, string> = {
  low: "#50fa7b",
  medium: "#f1fa8c",
  high: "#ffb86c",
  critical: "#ff5555",
};

const DECISION_COLORS: Record<Decision, string> = {
  allow: "#50fa7b",
  deny: "#ff5555",
  warn: "#f1fa8c",
};

const ENTITY_TYPES: EntityType[] = [
  "developer",
  "pipeline",
  "agent",
  "host",
  "service_account",
  "team",
];

// ── Helpers ───────────────────────────────────────────────────

function typeBadge(entityType: EntityType) {
  const color = ENTITY_TYPE_COLORS[entityType];
  return (
    <span
      style={{
        color: BG,
        backgroundColor: color,
        fontWeight: 700,
        fontSize: "0.65rem",
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        padding: "2px 8px",
        borderRadius: 3,
        whiteSpace: "nowrap",
      }}
    >
      {entityType.replace("_", " ")}
    </span>
  );
}

function severityBadge(severity: GuardrailSeverity) {
  const color = SEVERITY_COLORS[severity];
  return (
    <span
      style={{
        color: BG,
        backgroundColor: color,
        fontWeight: 700,
        fontSize: "0.6rem",
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        padding: "1px 6px",
        borderRadius: 3,
      }}
    >
      {severity}
    </span>
  );
}

function statusDot(active: boolean) {
  return (
    <span
      style={{
        display: "inline-block",
        width: 8,
        height: 8,
        borderRadius: "50%",
        backgroundColor: active ? ACCENT : "#6272a4",
        flexShrink: 0,
      }}
      title={active ? "Active" : "Inactive"}
    />
  );
}

// ── Component ─────────────────────────────────────────────────

export function AccessControlWorkbench() {
  const [tab, setTab] = useState<Tab>("entities");

  // ── Entities state ──────────────────────────────────────────
  const [entities, setEntities] = useState<Entity[]>([]);
  const [entitiesLoading, setEntitiesLoading] = useState(true);
  const [entitiesError, setEntitiesError] = useState<string | null>(null);
  const [showRegister, setShowRegister] = useState(false);
  const [registerName, setRegisterName] = useState("");
  const [registerType, setRegisterType] = useState<EntityType>("developer");
  const [registerBudget, setRegisterBudget] = useState("100");
  const [registering, setRegistering] = useState(false);
  const [assigningRole, setAssigningRole] = useState<string | null>(null);
  const [assignRoleId, setAssignRoleId] = useState("");

  // ── Roles state ─────────────────────────────────────────────
  const [roles, setRoles] = useState<Role[]>([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [rolesError, setRolesError] = useState<string | null>(null);

  // ── Permission test state ───────────────────────────────────
  const [testEntityId, setTestEntityId] = useState("");
  const [testCapabilityId, setTestCapabilityId] = useState("");
  const [testAction, setTestAction] = useState("");
  const [testSpentUsd, setTestSpentUsd] = useState("");
  const [testResult, setTestResult] = useState<CheckResult | null>(null);
  const [testLoading, setTestLoading] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);
  const [grantedCaps, setGrantedCaps] = useState<GrantedCapability[]>([]);
  const [capsEntityId, setCapsEntityId] = useState("");
  const [capsLoading, setCapsLoading] = useState(false);
  const [capsError, setCapsError] = useState<string | null>(null);

  // ── Data fetching ───────────────────────────────────────────

  const fetchEntities = useCallback(async () => {
    try {
      setEntitiesError(null);
      const res = await fetch("/api/entities");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setEntities(data.entities ?? []);
    } catch (e: unknown) {
      setEntitiesError(e instanceof Error ? e.message : "Failed to fetch entities");
    } finally {
      setEntitiesLoading(false);
    }
  }, []);

  const fetchRoles = useCallback(async () => {
    try {
      setRolesError(null);
      const res = await fetch("/api/roles");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRoles(data.roles ?? []);
    } catch (e: unknown) {
      setRolesError(e instanceof Error ? e.message : "Failed to fetch roles");
    } finally {
      setRolesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEntities();
    fetchRoles();
  }, [fetchEntities, fetchRoles]);

  // ── Entity actions ──────────────────────────────────────────

  const registerEntity = useCallback(async () => {
    if (!registerName.trim()) return;
    setRegistering(true);
    try {
      const res = await fetch("/api/entities", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: registerName.trim(),
          entity_type: registerType,
          budget_usd: parseFloat(registerBudget) || 0,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRegisterName("");
      setRegisterBudget("100");
      setShowRegister(false);
      await fetchEntities();
    } catch (e: unknown) {
      setEntitiesError(e instanceof Error ? e.message : "Failed to register entity");
    } finally {
      setRegistering(false);
    }
  }, [registerName, registerType, registerBudget, fetchEntities]);

  const assignRole = useCallback(
    async (entityId: string) => {
      if (!assignRoleId.trim()) return;
      try {
        const res = await fetch(`/api/entities/${entityId}/roles`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ role_id: assignRoleId.trim() }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setAssigningRole(null);
        setAssignRoleId("");
        await fetchEntities();
      } catch (e: unknown) {
        setEntitiesError(e instanceof Error ? e.message : "Failed to assign role");
      }
    },
    [assignRoleId, fetchEntities],
  );

  const removeRole = useCallback(
    async (entityId: string, roleId: string) => {
      try {
        const res = await fetch(`/api/entities/${entityId}/roles/${roleId}`, {
          method: "DELETE",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        await fetchEntities();
      } catch (e: unknown) {
        setEntitiesError(e instanceof Error ? e.message : "Failed to remove role");
      }
    },
    [fetchEntities],
  );

  // ── Permission check ───────────────────────────────────────

  const runCheck = useCallback(async () => {
    if (!testEntityId.trim() || !testCapabilityId.trim()) return;
    setTestLoading(true);
    setTestError(null);
    setTestResult(null);
    try {
      const body: Record<string, string | number> = {
        entity_id: testEntityId.trim(),
        capability_id: testCapabilityId.trim(),
      };
      if (testAction.trim()) body.action = testAction.trim();
      if (testSpentUsd.trim()) body.spent_usd = parseFloat(testSpentUsd);
      const res = await fetch("/api/auth/check", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setTestResult(data);
    } catch (e: unknown) {
      setTestError(e instanceof Error ? e.message : "Check failed");
    } finally {
      setTestLoading(false);
    }
  }, [testEntityId, testCapabilityId, testAction, testSpentUsd]);

  const fetchCapabilities = useCallback(async () => {
    if (!capsEntityId.trim()) return;
    setCapsLoading(true);
    setCapsError(null);
    setGrantedCaps([]);
    try {
      const res = await fetch("/api/auth/capabilities", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ entity_id: capsEntityId.trim() }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setGrantedCaps(data.capabilities ?? []);
    } catch (e: unknown) {
      setCapsError(e instanceof Error ? e.message : "Failed to fetch capabilities");
    } finally {
      setCapsLoading(false);
    }
  }, [capsEntityId]);

  // ── Shared styles ──────────────────────────────────────────

  const inputStyle: React.CSSProperties = {
    padding: "6px 10px",
    fontSize: "0.78rem",
    fontFamily: FONT,
    color: TEXT_BRIGHT,
    backgroundColor: "#1a1b26",
    border: `1px solid ${BORDER}`,
    borderRadius: 4,
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  };

  const selectStyle: React.CSSProperties = {
    ...inputStyle,
    appearance: "none",
    WebkitAppearance: "none",
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 8px center",
    paddingRight: 28,
  };

  const btnPrimary: React.CSSProperties = {
    padding: "6px 16px",
    fontSize: "0.72rem",
    fontFamily: FONT,
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: BG,
    backgroundColor: ACCENT,
    border: "none",
    borderRadius: 4,
    cursor: "pointer",
    transition: "opacity 0.15s ease",
    whiteSpace: "nowrap",
  };

  const btnSecondary: React.CSSProperties = {
    padding: "6px 16px",
    fontSize: "0.72rem",
    fontFamily: FONT,
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: TEXT_DIM,
    backgroundColor: "transparent",
    border: `1px solid ${BORDER}`,
    borderRadius: 4,
    cursor: "pointer",
    transition: "all 0.15s ease",
    whiteSpace: "nowrap",
  };

  const btnDanger: React.CSSProperties = {
    padding: "2px 8px",
    fontSize: "0.62rem",
    fontFamily: FONT,
    fontWeight: 600,
    color: ERROR,
    backgroundColor: "transparent",
    border: `1px solid ${ERROR}33`,
    borderRadius: 3,
    cursor: "pointer",
    transition: "all 0.15s ease",
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: CARD_BG,
    border: `1px solid ${BORDER}`,
    borderRadius: 6,
    padding: 16,
  };

  const labelStyle: React.CSSProperties = {
    fontSize: "0.68rem",
    color: TEXT_DIM,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontWeight: 600,
    marginBottom: 4,
  };

  // ── Render ─────────────────────────────────────────────────

  return (
    <main
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: BG,
        color: TEXT,
        fontFamily: FONT,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 20px",
          borderBottom: `1px solid ${BORDER}`,
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <svg
            viewBox="0 0 24 24"
            width={20}
            height={20}
            fill="none"
            stroke={ACCENT}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0110 0v4" />
          </svg>
          <span style={{ fontSize: "0.95rem", fontWeight: 700, color: ACCENT }}>
            Access Control
          </span>
          <span
            style={{
              fontSize: "0.65rem",
              color: TEXT_DIM,
              border: `1px solid ${BORDER}`,
              padding: "1px 6px",
              borderRadius: 3,
            }}
          >
            RBAC
          </span>
        </div>

        {/* Tabs */}
        <nav style={{ display: "flex", gap: 2 }}>
          {([
            { id: "entities" as Tab, label: "Entities" },
            { id: "roles" as Tab, label: "Roles" },
            { id: "test" as Tab, label: "Permission Test" },
          ]).map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                padding: "4px 14px",
                fontSize: "0.72rem",
                fontFamily: FONT,
                fontWeight: tab === t.id ? 700 : 400,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: tab === t.id ? BG : ACCENT,
                backgroundColor: tab === t.id ? ACCENT : "transparent",
                border: tab === t.id ? "none" : `1px solid ${BORDER}`,
                borderRadius: 3,
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </header>

      {/* Body */}
      <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
        {/* ── Entities Tab ─────────────────────────────────── */}
        {tab === "entities" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Toolbar */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span style={{ fontSize: "0.78rem", color: TEXT_DIM }}>
                {entities.length} entit{entities.length === 1 ? "y" : "ies"} registered
              </span>
              <button
                onClick={() => setShowRegister(!showRegister)}
                style={showRegister ? btnSecondary : btnPrimary}
              >
                {showRegister ? "Cancel" : "+ Register Entity"}
              </button>
            </div>

            {/* Register form */}
            {showRegister && (
              <div style={{ ...cardStyle, display: "flex", flexDirection: "column", gap: 12 }}>
                <span
                  style={{
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    color: TEXT_BRIGHT,
                  }}
                >
                  Register New Entity
                </span>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                  <div>
                    <div style={labelStyle}>Name</div>
                    <input
                      style={inputStyle}
                      value={registerName}
                      onChange={(e) => setRegisterName(e.target.value)}
                      placeholder="e.g. ci-runner-01"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") registerEntity();
                      }}
                    />
                  </div>
                  <div>
                    <div style={labelStyle}>Type</div>
                    <select
                      style={selectStyle}
                      value={registerType}
                      onChange={(e) => setRegisterType(e.target.value as EntityType)}
                    >
                      {ENTITY_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t.replace("_", " ")}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <div style={labelStyle}>Budget (USD)</div>
                    <input
                      style={inputStyle}
                      type="number"
                      min="0"
                      step="1"
                      value={registerBudget}
                      onChange={(e) => setRegisterBudget(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") registerEntity();
                      }}
                    />
                  </div>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button
                    onClick={registerEntity}
                    disabled={registering || !registerName.trim()}
                    style={{
                      ...btnPrimary,
                      opacity: registering || !registerName.trim() ? 0.5 : 1,
                    }}
                  >
                    {registering ? "Registering..." : "Register"}
                  </button>
                </div>
              </div>
            )}

            {/* Error */}
            {entitiesError && (
              <div
                style={{
                  ...cardStyle,
                  borderColor: `${ERROR}44`,
                  color: ERROR,
                  fontSize: "0.78rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>Error: {entitiesError}</span>
                <button
                  onClick={() => setEntitiesError(null)}
                  style={{
                    background: "none",
                    border: "none",
                    color: ERROR,
                    cursor: "pointer",
                    fontFamily: FONT,
                    fontSize: "0.85rem",
                  }}
                >
                  x
                </button>
              </div>
            )}

            {/* Loading */}
            {entitiesLoading && (
              <div style={{ textAlign: "center", color: TEXT_DIM, padding: 40 }}>
                Loading entities...
              </div>
            )}

            {/* Empty state */}
            {!entitiesLoading && entities.length === 0 && (
              <div
                style={{
                  ...cardStyle,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 12,
                  padding: 40,
                  color: TEXT_DIM,
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  width={32}
                  height={32}
                  fill="none"
                  stroke="rgba(255,255,255,0.06)"
                  strokeWidth={1.5}
                >
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <span style={{ fontSize: "0.8rem" }}>
                  No entities registered. Create one to get started.
                </span>
              </div>
            )}

            {/* Entity list */}
            {!entitiesLoading &&
              entities.map((entity) => (
                <div
                  key={entity.id}
                  style={{
                    ...cardStyle,
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  {/* Entity header row */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                    }}
                  >
                    {statusDot(entity.active)}
                    <span
                      style={{
                        fontSize: "0.85rem",
                        fontWeight: 700,
                        color: TEXT_BRIGHT,
                      }}
                    >
                      {entity.name}
                    </span>
                    {typeBadge(entity.entity_type)}
                    <span
                      style={{
                        marginLeft: "auto",
                        fontSize: "0.7rem",
                        color: TEXT_DIM,
                        fontFamily: "monospace",
                      }}
                    >
                      {entity.id}
                    </span>
                  </div>

                  {/* Budget + status row */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 16,
                      fontSize: "0.72rem",
                    }}
                  >
                    <span style={{ color: TEXT_DIM }}>
                      Budget:{" "}
                      <span style={{ color: ACCENT, fontWeight: 600 }}>
                        ${(entity.budget_usd ?? 0).toFixed(2)}
                      </span>
                    </span>
                    <span
                      style={{
                        color: entity.active ? ACCENT : TEXT_DIM,
                        fontSize: "0.65rem",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                      }}
                    >
                      {entity.active ? "active" : "inactive"}
                    </span>
                  </div>

                  {/* Roles */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexWrap: "wrap",
                      }}
                    >
                      <span style={{ ...labelStyle, marginBottom: 0 }}>Roles:</span>
                      {entity.roles.length === 0 && (
                        <span style={{ fontSize: "0.7rem", color: TEXT_DIM, fontStyle: "italic" }}>
                          none assigned
                        </span>
                      )}
                      {entity.roles.map((role) => (
                        <span
                          key={role.role_id}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            padding: "2px 8px",
                            fontSize: "0.65rem",
                            fontWeight: 600,
                            color: ACCENT,
                            backgroundColor: `${ACCENT}15`,
                            border: `1px solid ${ACCENT}33`,
                            borderRadius: 3,
                          }}
                        >
                          {role.name || role.role_id}
                          <button
                            onClick={() => removeRole(entity.id, role.role_id)}
                            title="Remove role"
                            style={{
                              background: "none",
                              border: "none",
                              color: TEXT_DIM,
                              cursor: "pointer",
                              fontFamily: FONT,
                              fontSize: "0.72rem",
                              padding: 0,
                              lineHeight: 1,
                              marginLeft: 2,
                            }}
                          >
                            x
                          </button>
                        </span>
                      ))}

                      {/* Assign role toggle */}
                      {assigningRole !== entity.id && (
                        <button
                          onClick={() => {
                            setAssigningRole(entity.id);
                            setAssignRoleId("");
                          }}
                          style={{
                            padding: "2px 8px",
                            fontSize: "0.62rem",
                            fontFamily: FONT,
                            fontWeight: 600,
                            color: TEXT_DIM,
                            backgroundColor: "transparent",
                            border: `1px dashed ${BORDER}`,
                            borderRadius: 3,
                            cursor: "pointer",
                          }}
                        >
                          + role
                        </button>
                      )}
                    </div>

                    {/* Inline assign role form */}
                    {assigningRole === entity.id && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                        {roles.length > 0 ? (
                          <select
                            style={{ ...selectStyle, width: 220 }}
                            value={assignRoleId}
                            onChange={(e) => setAssignRoleId(e.target.value)}
                          >
                            <option value="">Select role...</option>
                            {roles
                              .filter(
                                (r) =>
                                  r.allowed_entity_types.length === 0 ||
                                  r.allowed_entity_types.includes(entity.entity_type),
                              )
                              .map((r) => (
                                <option key={r.id} value={r.id}>
                                  {r.name}
                                </option>
                              ))}
                          </select>
                        ) : (
                          <input
                            style={{ ...inputStyle, width: 220 }}
                            value={assignRoleId}
                            onChange={(e) => setAssignRoleId(e.target.value)}
                            placeholder="Role ID"
                            onKeyDown={(e) => {
                              if (e.key === "Enter") assignRole(entity.id);
                            }}
                          />
                        )}
                        <button
                          onClick={() => assignRole(entity.id)}
                          disabled={!assignRoleId.trim()}
                          style={{
                            ...btnPrimary,
                            padding: "4px 12px",
                            fontSize: "0.65rem",
                            opacity: assignRoleId.trim() ? 1 : 0.5,
                          }}
                        >
                          Assign
                        </button>
                        <button
                          onClick={() => {
                            setAssigningRole(null);
                            setAssignRoleId("");
                          }}
                          style={{
                            ...btnSecondary,
                            padding: "4px 12px",
                            fontSize: "0.65rem",
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
          </div>
        )}

        {/* ── Roles Tab ────────────────────────────────────── */}
        {tab === "roles" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Header */}
            <span style={{ fontSize: "0.78rem", color: TEXT_DIM }}>
              {roles.length} role{roles.length === 1 ? "" : "s"} defined
            </span>

            {/* Error */}
            {rolesError && (
              <div
                style={{
                  ...cardStyle,
                  borderColor: `${ERROR}44`,
                  color: ERROR,
                  fontSize: "0.78rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <span>Error: {rolesError}</span>
                <button
                  onClick={() => setRolesError(null)}
                  style={{
                    background: "none",
                    border: "none",
                    color: ERROR,
                    cursor: "pointer",
                    fontFamily: FONT,
                    fontSize: "0.85rem",
                  }}
                >
                  x
                </button>
              </div>
            )}

            {/* Loading */}
            {rolesLoading && (
              <div style={{ textAlign: "center", color: TEXT_DIM, padding: 40 }}>
                Loading roles...
              </div>
            )}

            {/* Empty */}
            {!rolesLoading && roles.length === 0 && (
              <div
                style={{
                  ...cardStyle,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 12,
                  padding: 40,
                  color: TEXT_DIM,
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  width={32}
                  height={32}
                  fill="none"
                  stroke="rgba(255,255,255,0.06)"
                  strokeWidth={1.5}
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                <span style={{ fontSize: "0.8rem" }}>No roles defined.</span>
              </div>
            )}

            {/* Role cards */}
            {!rolesLoading &&
              roles.map((role) => (
                <div
                  key={role.id}
                  style={{
                    ...cardStyle,
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                    borderLeft: role.locked
                      ? `3px solid ${WARN}`
                      : `3px solid ${ACCENT}44`,
                  }}
                >
                  {/* Role header */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                    }}
                  >
                    <span
                      style={{
                        fontSize: "0.88rem",
                        fontWeight: 700,
                        color: TEXT_BRIGHT,
                      }}
                    >
                      {role.name}
                    </span>
                    {role.locked && (
                      <span
                        style={{
                          fontSize: "0.6rem",
                          fontWeight: 700,
                          color: WARN,
                          backgroundColor: `${WARN}18`,
                          border: `1px solid ${WARN}33`,
                          padding: "1px 6px",
                          borderRadius: 3,
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                        }}
                      >
                        locked
                      </span>
                    )}
                    <span
                      style={{
                        marginLeft: "auto",
                        fontSize: "0.68rem",
                        color: TEXT_DIM,
                        fontFamily: "monospace",
                      }}
                    >
                      {role.id}
                    </span>
                  </div>

                  {/* Budget + entity types */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 16,
                      fontSize: "0.72rem",
                    }}
                  >
                    <span style={{ color: TEXT_DIM }}>
                      Budget limit:{" "}
                      <span style={{ color: ACCENT, fontWeight: 600 }}>
                        ${(role.budget_limit_usd ?? 0).toFixed(2)}
                      </span>
                    </span>
                    {role.allowed_entity_types.length > 0 && (
                      <span style={{ color: TEXT_DIM }}>
                        Assignable to:{" "}
                        {role.allowed_entity_types.map((t) => (
                          <span
                            key={t}
                            style={{
                              color: ENTITY_TYPE_COLORS[t],
                              fontWeight: 600,
                              marginRight: 6,
                            }}
                          >
                            {t.replace("_", " ")}
                          </span>
                        ))}
                      </span>
                    )}
                    {role.allowed_entity_types.length === 0 && (
                      <span style={{ color: TEXT_DIM, fontStyle: "italic" }}>
                        Assignable to: all types
                      </span>
                    )}
                  </div>

                  {/* Capabilities */}
                  <div>
                    <div style={{ ...labelStyle, marginBottom: 6 }}>Capabilities</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {role.capabilities.length === 0 && (
                        <span
                          style={{
                            fontSize: "0.7rem",
                            color: TEXT_DIM,
                            fontStyle: "italic",
                          }}
                        >
                          none
                        </span>
                      )}
                      {role.capabilities.map((cap) => (
                        <span
                          key={cap}
                          style={{
                            padding: "2px 8px",
                            fontSize: "0.68rem",
                            fontWeight: 600,
                            fontFamily: "monospace",
                            color: "#7c9fff",
                            backgroundColor: "#7c9fff12",
                            border: "1px solid #7c9fff28",
                            borderRadius: 3,
                          }}
                        >
                          {cap}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Guardrails */}
                  {role.guardrails.length > 0 && (
                    <div>
                      <div style={{ ...labelStyle, marginBottom: 6 }}>Guardrails</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {role.guardrails.map((g) => (
                          <div
                            key={g.id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              padding: "4px 8px",
                              backgroundColor: "#1a1b26",
                              borderRadius: 3,
                              border: `1px solid ${BORDER}`,
                              fontSize: "0.72rem",
                            }}
                          >
                            {severityBadge(g.severity)}
                            <span style={{ color: TEXT }}>{g.description}</span>
                            <span
                              style={{
                                marginLeft: "auto",
                                fontSize: "0.62rem",
                                color: TEXT_DIM,
                                fontFamily: "monospace",
                              }}
                            >
                              {g.id}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
          </div>
        )}

        {/* ── Permission Test Tab ──────────────────────────── */}
        {tab === "test" && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 20,
              alignItems: "start",
            }}
          >
            {/* Left: Auth check */}
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ ...cardStyle, display: "flex", flexDirection: "column", gap: 14 }}>
                <span style={{ fontSize: "0.85rem", fontWeight: 700, color: TEXT_BRIGHT }}>
                  Permission Check
                </span>
                <span style={{ fontSize: "0.72rem", color: TEXT_DIM }}>
                  Test whether an entity is allowed to perform a capability.
                </span>

                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <div>
                    <div style={labelStyle}>Entity</div>
                    {entities.length > 0 ? (
                      <select
                        style={selectStyle}
                        value={testEntityId}
                        onChange={(e) => setTestEntityId(e.target.value)}
                      >
                        <option value="">Select entity...</option>
                        {entities.map((ent) => (
                          <option key={ent.id} value={ent.id}>
                            {ent.name} ({ent.entity_type})
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        style={inputStyle}
                        value={testEntityId}
                        onChange={(e) => setTestEntityId(e.target.value)}
                        placeholder="Entity ID"
                      />
                    )}
                  </div>
                  <div>
                    <div style={labelStyle}>Capability</div>
                    <input
                      style={inputStyle}
                      value={testCapabilityId}
                      onChange={(e) => setTestCapabilityId(e.target.value)}
                      placeholder="e.g. code:write, deploy:production"
                    />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div>
                      <div style={labelStyle}>Action (optional)</div>
                      <input
                        style={inputStyle}
                        value={testAction}
                        onChange={(e) => setTestAction(e.target.value)}
                        placeholder="e.g. execute"
                      />
                    </div>
                    <div>
                      <div style={labelStyle}>Spent USD (optional)</div>
                      <input
                        style={inputStyle}
                        type="number"
                        min="0"
                        step="0.01"
                        value={testSpentUsd}
                        onChange={(e) => setTestSpentUsd(e.target.value)}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>

                <button
                  onClick={runCheck}
                  disabled={testLoading || !testEntityId.trim() || !testCapabilityId.trim()}
                  style={{
                    ...btnPrimary,
                    opacity:
                      testLoading || !testEntityId.trim() || !testCapabilityId.trim()
                        ? 0.5
                        : 1,
                    alignSelf: "flex-start",
                  }}
                >
                  {testLoading ? "Checking..." : "Run Check"}
                </button>
              </div>

              {/* Result card */}
              {testError && (
                <div
                  style={{
                    ...cardStyle,
                    borderColor: `${ERROR}44`,
                    color: ERROR,
                    fontSize: "0.78rem",
                  }}
                >
                  Error: {testError}
                </div>
              )}

              {testResult && (
                <div
                  style={{
                    ...cardStyle,
                    borderColor: `${DECISION_COLORS[testResult.decision]}55`,
                    borderLeft: `4px solid ${DECISION_COLORS[testResult.decision]}`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      marginBottom: 12,
                    }}
                  >
                    {/* Decision icon */}
                    <svg
                      viewBox="0 0 24 24"
                      width={28}
                      height={28}
                      fill="none"
                      stroke={DECISION_COLORS[testResult.decision]}
                      strokeWidth={2.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      {testResult.decision === "allow" && (
                        <path d="M20 6L9 17l-5-5" />
                      )}
                      {testResult.decision === "deny" && (
                        <>
                          <circle cx="12" cy="12" r="10" strokeWidth={2} />
                          <path d="M15 9l-6 6M9 9l6 6" />
                        </>
                      )}
                      {testResult.decision === "warn" && (
                        <>
                          <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" strokeWidth={2} />
                          <path d="M12 9v4M12 17h.01" />
                        </>
                      )}
                    </svg>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span
                        style={{
                          fontSize: "1.1rem",
                          fontWeight: 800,
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          color: DECISION_COLORS[testResult.decision],
                        }}
                      >
                        {testResult.decision}
                      </span>
                      <span style={{ fontSize: "0.68rem", color: TEXT_DIM }}>
                        {testResult.entity_id} / {testResult.capability_id}
                      </span>
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: "0.78rem",
                      color: TEXT,
                      padding: "10px 12px",
                      backgroundColor: "#1a1b26",
                      borderRadius: 4,
                      border: `1px solid ${BORDER}`,
                      lineHeight: 1.6,
                    }}
                  >
                    {testResult.reason}
                  </div>
                </div>
              )}
            </div>

            {/* Right: Capabilities lookup */}
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ ...cardStyle, display: "flex", flexDirection: "column", gap: 14 }}>
                <span style={{ fontSize: "0.85rem", fontWeight: 700, color: TEXT_BRIGHT }}>
                  Granted Capabilities
                </span>
                <span style={{ fontSize: "0.72rem", color: TEXT_DIM }}>
                  Look up all capabilities granted to an entity.
                </span>

                <div>
                  <div style={labelStyle}>Entity</div>
                  {entities.length > 0 ? (
                    <select
                      style={selectStyle}
                      value={capsEntityId}
                      onChange={(e) => setCapsEntityId(e.target.value)}
                    >
                      <option value="">Select entity...</option>
                      {entities.map((ent) => (
                        <option key={ent.id} value={ent.id}>
                          {ent.name} ({ent.entity_type})
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      style={inputStyle}
                      value={capsEntityId}
                      onChange={(e) => setCapsEntityId(e.target.value)}
                      placeholder="Entity ID"
                    />
                  )}
                </div>

                <button
                  onClick={fetchCapabilities}
                  disabled={capsLoading || !capsEntityId.trim()}
                  style={{
                    ...btnPrimary,
                    opacity: capsLoading || !capsEntityId.trim() ? 0.5 : 1,
                    alignSelf: "flex-start",
                  }}
                >
                  {capsLoading ? "Loading..." : "Fetch Capabilities"}
                </button>
              </div>

              {/* Caps error */}
              {capsError && (
                <div
                  style={{
                    ...cardStyle,
                    borderColor: `${ERROR}44`,
                    color: ERROR,
                    fontSize: "0.78rem",
                  }}
                >
                  Error: {capsError}
                </div>
              )}

              {/* Caps results */}
              {grantedCaps.length > 0 && (
                <div style={{ ...cardStyle, display: "flex", flexDirection: "column", gap: 8 }}>
                  <span style={{ ...labelStyle, marginBottom: 2 }}>
                    {grantedCaps.length} capabilit{grantedCaps.length === 1 ? "y" : "ies"} granted
                  </span>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {grantedCaps.map((cap) => (
                      <div
                        key={cap.capability_id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "6px 10px",
                          backgroundColor: "#1a1b26",
                          borderRadius: 3,
                          border: `1px solid ${BORDER}`,
                        }}
                      >
                        <span
                          style={{
                            fontFamily: "monospace",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            color: ACCENT,
                          }}
                        >
                          {cap.capability_id}
                        </span>
                        <span style={{ fontSize: "0.65rem", color: TEXT_DIM }}>
                          via {cap.source_role}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty caps */}
              {!capsLoading && grantedCaps.length === 0 && capsEntityId && !capsError && (
                <div
                  style={{
                    ...cardStyle,
                    textAlign: "center",
                    color: TEXT_DIM,
                    fontSize: "0.78rem",
                    padding: 24,
                  }}
                >
                  No capabilities found for this entity.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
